'use client';

import { useLayoutEffect, useRef, useState } from 'react';
import { calculateSegmentVisibleCount } from './calculate-segment-visible-count';

type UseSegmentVisibleCountParams = {
  itemCount: number;
  maxItemsToDisplay?: number;
};

export function useSegmentVisibleCount({
  itemCount,
  maxItemsToDisplay,
}: UseSegmentVisibleCountParams) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const moreMeasureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(itemCount);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) {
      return;
    }

    const recalculate = () => {
      const itemElements = Array.from(measure.children) as HTMLElement[];
      setVisibleCount(
        calculateSegmentVisibleCount({
          itemWidths: itemElements.map((element) => element.offsetWidth),
          moreWidth: moreMeasureRef.current?.offsetWidth ?? 0,
          availableWidth: container.clientWidth,
          maxItemsToDisplay,
        }),
      );
    };

    recalculate();
    const observer = new ResizeObserver(recalculate);
    observer.observe(container);
    return () => observer.disconnect();
  }, [itemCount, maxItemsToDisplay]);

  return {
    containerRef,
    measureRef,
    moreMeasureRef,
    visibleCount: Math.min(visibleCount, itemCount),
  };
}
